import React, { CSSProperties, FC, useCallback, useEffect, useRef, useState } from 'react';
import HlsVideo from '../HlsVideo';
import { clearRtspHlsSession, getRtspHlsSession } from './rtsp';

interface RtspPlayerFrameProps {
  rtspUrl?: string;
  width?: number | string;
  height?: number | string;
  style?: CSSProperties;
  objectFit?: CSSProperties['objectFit'];
  backgroundColor?: CSSProperties['backgroundColor'];
}

const RtspPlayerFrame: FC<RtspPlayerFrameProps> = ({
  rtspUrl,
  width = '100%',
  height = '100%',
  style,
  objectFit,
  backgroundColor,
}) => {
  const [hlsUrl, setHlsUrl] = useState<string>();
  const [hasError, setHasError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const reconnectTimerRef = useRef<number>();

  const handleUnauthorized = useCallback(() => {
    if (reconnectTimerRef.current) {
      return;
    }

    clearRtspHlsSession(rtspUrl);
    setHlsUrl(undefined);

    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = undefined;
      setReloadKey(key => key + 1);
    }, 5000);
  }, [rtspUrl]);

  useEffect(() => {
    return () => {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const connect = async () => {
      setHasError(false);
      setHlsUrl(undefined);
      if (!rtspUrl) {
        setHasError(true);
        return;
      }

      const session = await getRtspHlsSession(rtspUrl, reloadKey > 0);
      if (cancelled) return;

      if (!session?.url) {
        setHasError(true);
        return;
      }

      setHlsUrl(session.url);
    };

    connect();

    return () => {
      cancelled = true;
    };
  }, [reloadKey, rtspUrl]);

  if (hasError) {
    return <div style={{ width, height, ...style }}>RTSP播放地址无效</div>;
  }

  return (
    <div style={{ width, height, backgroundColor, ...style }}>
      <HlsVideo
        src={hlsUrl}
        width="100%"
        height="100%"
        minHeight={0}
        objectFit={objectFit}
        backgroundColor={backgroundColor}
        onRecoverableNetworkError={handleUnauthorized}
      />
    </div>
  );
};

export default RtspPlayerFrame;
